/**
 * Create form for one tutorial video.
 */
import * as React from 'react';
import { Clapperboard } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileDropzone } from '@/components/common/FileDropzone';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { Input } from '@/components/forms/Input';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Select } from '@/components/forms/Select';
import { toast } from '@/components/ui/toast';
import { getRoles } from '@/services/roles.service';
import { createTutorialVideo } from '@/services/tutorials.service';
import type { TutorialVideoFormState, TutorialVideoMode } from '@/types/tutorial.types';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  TUTORIAL_AUDIENCE_ROLE_GROUPS,
  TUTORIAL_MODE_OPTIONS,
  TUTORIAL_VIDEO_ACCEPTED_FORMATS,
  TUTORIAL_VIDEO_MAX_SIZE_BYTES,
  tutorialKeys,
} from './tutorial-configs';
import {
  applyTutorialVideoFile,
  applyTutorialVideoMode,
  applyTutorialVideoName,
  applyTutorialVideoRoleKeys,
  buildTutorialRoleChoices,
  emptyTutorialVideoForm,
  isTutorialVideoCreateFormValid,
  selectedChoiceKeys,
  tutorialRoleSelectOptions,
} from './tutorial-form-utils';

/** Props for {@link CreateTutorialFormView}. */
interface CreateTutorialFormViewProps {
  onClose: () => void;
  onSuccess: () => void;
}

/** Slide-panel create form: file, name, mode, then roles that match the mode. */
export const CreateTutorialFormView: React.FC<CreateTutorialFormViewProps> = ({
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = React.useState<TutorialVideoFormState>(emptyTutorialVideoForm);

  const rolesQuery = useQuery({
    queryKey: tutorialKeys.roles,
    queryFn: () => getRoles({ roleGroups: [...TUTORIAL_AUDIENCE_ROLE_GROUPS] }),
  });

  const roles = rolesQuery.data?.data ?? [];
  const roleChoices = buildTutorialRoleChoices(roles, form.mode);
  const selectedRoles = selectedChoiceKeys(roleChoices, form.roleIds);
  const roleOptions = tutorialRoleSelectOptions(roleChoices);

  const saveMutation = useMutation({
    mutationFn: () => createTutorialVideo(form),
    onSuccess: () => {
      toast.success('Vidéo ajoutée');
      onSuccess();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isValid = isTutorialVideoCreateFormValid(form);

  /** POST the new tutorial when the create form is valid. */
  function handleSubmit(): void {
    if (!isValid) return;
    saveMutation.mutate();
  }

  function handleFileSelect(file: File | null): void {
    setForm((current) => applyTutorialVideoFile(current, file));
  }

  function handleNameChange(event: React.ChangeEvent<HTMLInputElement>): void {
    setForm((current) => applyTutorialVideoName(current, event.target.value));
  }

  function handleModeChange(mode: string): void {
    setForm((current) => applyTutorialVideoMode(current, mode as TutorialVideoMode, roles));
  }

  function handleRolesChange(keys: string[]): void {
    setForm((current) => applyTutorialVideoRoleKeys(current, roleChoices, keys));
  }

  return (
    <SlidePanelForm
      formType="create"
      title="Ajouter une vidéo"
      subtitle="Visible uniquement pour les rôles sélectionnés"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Ajouter"
      isSubmitting={saveMutation.isPending}
      isValid={isValid}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <Clapperboard className="w-4 h-4" />
          Vidéo
        </h4>
        <div className="grid grid-cols-1 gap-4">
          <FileDropzone
            label="Fichier vidéo"
            required
            accept={TUTORIAL_VIDEO_ACCEPTED_FORMATS}
            maxSize={TUTORIAL_VIDEO_MAX_SIZE_BYTES}
            onFileSelect={handleFileSelect}
            hint="MP4, WEBM ou MOV — taille maximale : 50 Mo"
          />
          <Input
            label="Nom"
            value={form.name}
            onChange={handleNameChange}
            placeholder="Ex: Présentation plateforme tuteur"
            maxLength={200}
            required
          />
          <Select
            label="Mode"
            options={TUTORIAL_MODE_OPTIONS}
            value={form.mode}
            onChange={handleModeChange}
            required
          />
          <MultiSelect
            label="Rôles"
            placeholder="Aucun rôle — visible seulement par l'administration"
            options={roleOptions}
            value={selectedRoles}
            onChange={handleRolesChange}
            helperText="Les rôles suivent le mode. L'administration est toujours incluse."
          />
        </div>
      </div>
    </SlidePanelForm>
  );
};
